/**
 * trendDeltas.ts — 2.1b (§16.2.4, ruled §22.5, wire §22.6, copy P2-15): the
 * render rules for the k-metric delta line, from the /v1/sales/reports wire
 * ONLY.
 *
 * The wire carries ONE captured prior day per member ({prior, asOf} | null)
 * beside the live figure the same payload already shows; the screen takes
 * the difference and names the day it is against ("vs 1 Aug" — a gap moves
 * asOf and the copy says so, never silently). A null member is ABSENT: the
 * line renders "—" followed by the server's reason sentence (deltas.absent),
 * never a 0 and never hidden — R3 on the screen. historySince renders as
 * "History since 7 September": the tenant's first captured day, the floor no
 * delta can reach past.
 *
 * Nothing here recomputes a prior from the deals list. The prior is the
 * server's captured figure or it is absent; the only arithmetic is the
 * subtraction §22.6 assigns to the screen.
 */
import type { SalesReportDelta } from '../../../../api/types'

export const ABSENT_DELTA = '—'
export type DeltaTone = 'up' | 'down' | 'flat' | 'absent'
export interface DeltaLine { text: string; tone: DeltaTone }

// Fixed tables, no locale: the ISO date is sliced, never parsed through Date,
// so a browser timezone can never move "2026-08-01" to 31 July.
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function dayAndMonth(iso: string): { day: number; month: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  const month = Number(m[2]); const day = Number(m[3])
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return { day, month }
}

/** "2026-08-01" → "1 Aug" (the §22.5 "vs 1 Oct" form). An unparseable
 *  date echoes verbatim rather than inventing one. */
export function shortDay(iso: string): string {
  const p = dayAndMonth(iso)
  return p ? `${p.day} ${MONTH_SHORT[p.month - 1]}` : iso
}

/** "2026-09-07" → "7 September" (the P2-15 "History since" form). */
export function longDay(iso: string): string {
  const p = dayAndMonth(iso)
  return p ? `${p.day} ${MONTH_LONG[p.month - 1]}` : iso
}

/**
 * The delta line for one member.
 *   member undefined → null: the wire carries no such member (a pre-2.1b
 *     backend) — nothing to render and nothing to hide.
 *   member null → "— <reason>": absent, with the server's sentence. "—" alone
 *     only if the wire broke its own contract and sent no reason.
 *   current not a number → "—": a prior exists but the card itself shows no
 *     live figure to compare against (avg deal size with no open deals).
 *   otherwise → "▲ £12k vs 1 Aug" / "▼ …" / "→ no change vs 1 Aug", the
 *     difference current − prior, formatted by the card's own formatter.
 */
export function deltaLine(
  member: SalesReportDelta | null | undefined,
  current: number | null | undefined,
  reason: string | undefined,
  fmt: (n: number) => string,
): DeltaLine | null {
  if (member === undefined) return null
  if (member === null) return { text: reason ? `${ABSENT_DELTA} ${reason}` : ABSENT_DELTA, tone: 'absent' }
  if (current == null || !Number.isFinite(current)) return { text: ABSENT_DELTA, tone: 'absent' }
  const diff = current - member.prior
  const day = shortDay(member.asOf)
  if (diff === 0) return { text: `→ no change vs ${day}`, tone: 'flat' }
  return { text: `${diff > 0 ? '▲' : '▼'} ${fmt(Math.abs(diff))} vs ${day}`, tone: diff > 0 ? 'up' : 'down' }
}

/** The page's history floor. undefined (pre-2.1b wire) → null, nothing to
 *  render; null (no capture yet) → the words; a date → "History since 7
 *  September". */
export function historySinceLine(historySince: string | null | undefined): string | null {
  if (historySince === undefined) return null
  if (historySince === null) return 'No snapshot history yet'
  return `History since ${longDay(historySince)}`
}
